import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { DocStatus } from "../api/types";
import { formatDate } from "../lib/format";
import { StatusBadge } from "./StatusBadge";

/**
 * The shared ARTICLE-UNIT LIST CARD (RULE ZERO — one list look). Extracted from
 * the Articles landing so the Search screen renders matched units with the EXACT
 * same card: title (struck through when superseded) + brand · lang · resource
 * count meta + the { brief, research, article } role chips + `<StatusBadge>`,
 * linking straight into `<ArticleWorkbench>` at `/w/:slug/article/:id`.
 *
 * `footer` is an optional slot rendered inside the card body (below the chips) —
 * the Search screen fills it with the matched-resource snippet + a "matched in …"
 * hint; the Articles landing passes nothing, so its rendering is unchanged.
 * `rowTestId` / `rolesTestId` let each caller scope its own `data-testid`s
 * (Articles: `article-row-*` / `article-roles-*`; Search: `search-unit-*`).
 */

/** The three role slots of every article unit, in carve order (design §1.2). The
 *  card lights the first `resource_count` as present (brief is always slot 0). */
export const ARTICLE_ROLES: readonly { key: string; label: string }[] = [
  { key: "brief", label: "Brief" },
  { key: "research", label: "Research" },
  { key: "article", label: "Article" },
];

/** The lean unit shape the card renders. Both `ArticleUnitSummary` (Articles) and
 *  `SearchUnitHit` (Search) satisfy it structurally. */
export interface ArticleCardUnit {
  id: string;
  title: string;
  status: DocStatus;
  brand: string;
  lang: string;
  resource_count: number;
  delivered_at?: string | null;
}

export function ArticleUnitRow({
  slug,
  unit,
  rowTestId,
  rolesTestId,
  footer,
  actions,
}: {
  slug: string;
  unit: ArticleCardUnit;
  rowTestId?: string;
  rolesTestId?: string;
  footer?: ReactNode;
  /**
   * OPTIONAL trailing slot beside the status badge — the Articles landing fills
   * it with the per-row lifecycle menu (`<ArticleRowMenu>`); the Search screen
   * passes nothing and renders exactly as before.
   *
   * Whatever goes in here is INSIDE the row's single `<Link>`, so it must
   * `preventDefault()` + `stopPropagation()` on its own controls or interacting
   * with it navigates to the workbench.
   */
  actions?: ReactNode;
}) {
  const rid = rowTestId ?? `article-row-${unit.id}`;
  const rolesId = rolesTestId ?? `article-roles-${unit.id}`;
  return (
    <Link to={`/w/${slug}/article/${unit.id}`} className="docrow" data-testid={rid}>
      <div className="docrow__main">
        <div
          className={`docrow__title${
            unit.status === "superseded" ? " docrow__title--superseded" : ""
          }`}
        >
          {unit.title}
        </div>
        <div className="docrow__meta">
          <span>{unit.brand}</span>
          <span className="dot">·</span>
          <span>{unit.lang.toUpperCase()}</span>
          <span className="dot">·</span>
          <span>
            {unit.resource_count} resource{unit.resource_count === 1 ? "" : "s"}
          </span>
          {unit.delivered_at ? (
            <>
              <span className="dot">·</span>
              <span>Delivered {formatDate(unit.delivered_at)}</span>
            </>
          ) : null}
        </div>
        <div
          className="artrow__roles"
          data-testid={rolesId}
          title={`${unit.resource_count} of ${ARTICLE_ROLES.length} resources`}
        >
          {ARTICLE_ROLES.map((role, i) => {
            const present = i < unit.resource_count;
            return (
              <span
                key={role.key}
                className={`role-chip role-chip--${present ? "on" : "off"}`}
                data-present={present ? "1" : "0"}
              >
                {role.label}
              </span>
            );
          })}
        </div>
        {footer}
      </div>
      <div className="docrow__right">
        <StatusBadge status={unit.status} />
        {actions}
      </div>
    </Link>
  );
}
